/**
 * CompletedList — the "Ferdig" tab body on /oppgavebunken (Wave 4).
 *
 * Renders the whole family's approved chores, newest approval first, as the
 * visible tail of the verify loop: who did it, how many points it paid, who
 * approved it, and the proof photo when there is one.
 *
 * Photo gating: the feed's `proof_visible` flag already encodes the proofs
 * route's auth rule (admin OR the doer), so a sibling sees the row without a
 * thumbnail rather than a tile that 403s. The retention footer counts down
 * from approved_at using the same 30-day contract as ApprovalCard.
 *
 * Grouped by calendar day so a week of chores reads as a diary rather than an
 * undifferentiated list. Day headings use the shared nb-NO date formatter.
 *
 * #154 (steg 4b): Ferdig er ikke lenger en fane øverst, men en knapp nederst
 * på tavla. Radene er glass med radius 20 som TaskRow, med bildet til høyre.
 */
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChoreIcon, type TintSlot } from '../../components/primitives/ChoreIcon';
import { PhotoProofThumb } from '../../components/primitives/PhotoProofThumb';
import { RelativeTime } from '../../components/primitives/RelativeTime';
import { EmptyState } from '../../components/primitives';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { SectionLabel } from '../../components/primitives/SectionLabel';
import type { CompletedChore } from './useChoresQuery';

const DAY_MS = 86_400_000;
const RETENTION_DAYS = 30;

function retentionDaysLeft(approvedAtIso: string | null, now: Date): number {
  if (!approvedAtIso) return RETENTION_DAYS;
  const elapsed = Math.floor((now.getTime() - new Date(approvedAtIso).getTime()) / DAY_MS);
  return Math.max(0, RETENTION_DAYS - elapsed);
}

/** Group key = local calendar date of the approval, so 'i dag' stays one block. */
function dayKey(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function groupByDay(rows: readonly CompletedChore[]): Array<[string, CompletedChore[]]> {
  const groups = new Map<string, CompletedChore[]>();
  for (const row of rows) {
    const key = dayKey(row.approved_at);
    const list = groups.get(key);
    if (list) list.push(row);
    else groups.set(key, [row]);
  }
  return [...groups.entries()];
}

interface CompletedListProps {
  rows: readonly CompletedChore[];
  /** Override for tests; defaults to live wall clock. */
  now?: Date;
}

export function CompletedList({ rows, now }: CompletedListProps): ReactNode {
  const { t, i18n } = useTranslation();
  const wallClock = now ?? new Date();

  if (rows.length === 0) {
    return (
      <section data-testid="completed-empty">
        <EmptyState
          variant="page"
          heading={t('chores.completed.empty.heading')}
          body={t('chores.completed.empty.body')}
        />
      </section>
    );
  }

  const dayFormatter = new Intl.DateTimeFormat(i18n.language || 'nb-NO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <section data-testid="completed-list" className="flex flex-col gap-5">
      {groupByDay(rows).map(([key, dayRows]) => (
        <div key={key} className="flex flex-col">
          <SectionLabel as="h3" data-testid="completed-day-heading">
            {dayFormatter.format(new Date(dayRows[0]!.approved_at ?? Date.now()))}
          </SectionLabel>
          <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
            {dayRows.map((row) => (
              <li
                key={row.id}
                data-testid="completed-row"
                data-instance={row.id}
                className={`${GLASS_SURFACE} flex items-start gap-3 rounded-row p-3`}
              >
                <ChoreIcon
                  code={row.icon_key ?? 'sparkle'}
                  tintSlot={(row.tint_snapshot ?? 'sage') as TintSlot}
                  size={32}
                />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <p className="text-body font-bold leading-tight text-ink">
                    {row.title_snapshot}
                  </p>
                  <p className="text-caption text-ink-2">
                    {t('chores.completed.byline', {
                      name: row.claimant_short_name ?? t('chores.completed.unknownDoer'),
                      points: row.point_value_snapshot,
                    })}
                    {row.approved_at ? (
                      <>
                        {' · '}
                        <RelativeTime instant={row.approved_at} />
                      </>
                    ) : null}
                  </p>
                  {row.approver_short_name ? (
                    <p data-testid="completed-approver" className="text-caption text-ink-2">
                      {t('chores.completed.approvedBy', { name: row.approver_short_name })}
                    </p>
                  ) : null}
                </div>
                {row.proof_visible && row.proof_photo_url ? (
                  <PhotoProofThumb
                    url={row.proof_photo_url}
                    alt={t('chores.completed.photoAlt', {
                      name: row.claimant_short_name ?? t('chores.completed.unknownDoer'),
                    })}
                    size={88}
                    retainsLeftDays={retentionDaysLeft(row.approved_at, wallClock)}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}
